import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import * as numbers from "common/numbers";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import Visible from "components/common/Visible";
import NumberFormat from "react-number-format";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";

const SnapshotPricingRow = ({ layer }) => {
  return (
    <TableRow>
      <Visible byTag={"<SnapshotPricing>.status"} defaultVisible>
        <TableCell align={"center"}>
          <StatusBadgeV2 status={layer.status} variant={"chip"} />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.ourLimit"} defaultVisible>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            prefix={"$"}
            value={layer.lineSize}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.partOf"} defaultVisible>
        <TableCell align={"center"}>
          {numbers.abbreviated(layer.limit)}
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.excess"} defaultVisible>
        <TableCell align={"center"}>
          {numbers.abbreviated(layer.attachment)}
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.premium"} defaultVisible>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            prefix={"$"}
            value={layer.grossPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.rpm"} defaultVisible>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            prefix={"$"}
            value={layer.grossPPM}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.ourGross"} defaultVisible>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            prefix={"$"}
            value={layer.shareOfPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.ourNet"} defaultVisible>
        <TableCell align={"right"}>
          <NumberFormat
            displayType={"text"}
            thousandSeparator={true}
            decimalScale={0}
            prefix={"$"}
            value={((100 - layer.tac) / 100) * layer.shareOfPremium}
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.tac"} defaultVisible>
        <TableCell align={"center"}>
          {layer.tac}
          {"%"}
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.targetPrice"} defaultVisible>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            decimalScale={0}
            suffix={"%"}
            value={layer.uwSelectedMeasure?.targetPrice}
            className={
              (layer.uwSelectedMeasure?.targetPrice ?? 100) - 100 < 0
                ? "table-cell-bad-deal"
                : "table-cell-good-deal"
            }
          />
        </TableCell>
      </Visible>

      <Visible byTag={"<SnapshotPricing>.gnulr"} defaultVisible>
        <TableCell align={"center"}>
          <NumberFormat
            displayType={"text"}
            decimalScale={2}
            suffix={"%"}
            value={layer.uwSelectedMeasure?.technicalRatio}
          />
        </TableCell>
      </Visible>
    </TableRow>
  );
};

const SnapshotPricingTable = ({ pricingLayers }) => {
  return (
    <TableContainer>
      <Table size={"small"}>
        <TableHead>
          <TableRow>
            <Visible byTag={"<SnapshotPricing>.status"} defaultVisible>
              <TableCell align={"center"}>{"Status"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.ourLimit"} defaultVisible>
              <TableCell align={"right"}>{"Our Limit"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.partOf"} defaultVisible>
              <TableCell align={"center"}>{"Part Of"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.excess"} defaultVisible>
              <TableCell align={"center"}>{"Excess"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.premium"} defaultVisible>
              <TableCell align={"right"}>{"Premium"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.rpm"} defaultVisible>
              <TableCell align={"right"}>{"RPM"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.ourGross"} defaultVisible>
              <TableCell align={"right"}>{"Our Gross"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.ourNet"} defaultVisible>
              <TableCell align={"right"}>{"Our Net"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.tac"} defaultVisible>
              <TableCell align={"center"}>{"TAC"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.targetPrice"} defaultVisible>
              <TableCell align={"center"}>{"TP"}</TableCell>
            </Visible>
            <Visible byTag={"<SnapshotPricing>.gnulr"} defaultVisible>
              <TableCell align={"center"}>{"GNULR"}</TableCell>
            </Visible>
          </TableRow>
        </TableHead>
        <TableBody>
          {[...pricingLayers]
            .sort((a, b) => b.attachment - a.attachment)
            .map((layer, index) => (
              <SnapshotPricingRow
                layer={layer}
                key={`SNAPSHOT_PRICING_${index}`}
              />
            ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const SnapshotPricing = ({ state }) => {
  const pricingLayers = pricingSelectors.getTower(state);

  return state == null ? (
    <Typography padding={1.75}>
      {
        "No version information available, if this is a new submission you'll need to save it first"
      }
    </Typography>
  ) : (
    <SnapshotPricingTable pricingLayers={pricingLayers} />
  );
};

export default SnapshotPricing;
